// The LIBRARY-COPY slot — how the publication form offers "this issue is also in the
// library" without the CMS importing @erp/module-lib (ADR-010 §2.12; the slot the
// 2026-09-20 capabilities entry promised, its shape fixed in modules/cms/decisions.md
// 2026-09-21).
//
// WHY: `cms_publication.library_ebook_id` (db-cms 0024) is an OPAQUE LIB ebook id the
// CMS stores and never joins. Picking one needs LIB's ebook search — and inside a
// module that is the `cms → lib` edge ADR-001 Fig 4.1 bans outright (it would fuse
// the CMS chunk with the LIB chunk and make the CMS unshippable to an install with no
// library). The install's capabilities manifest does not lift the ban; it decides
// whether the hole is FILLED.
//
// So the CMS names the HOLE and the shell fills it — the @erp/ui/record-history and
// @erp/module-fin/fe/invoiceable-charges shape. `apps/portals/admin/src/app.tsx`, the
// one place two modules legally meet (Law F1), provides a picker over LIB's ebook query
// ONLY when `caps.has("lib")`; anywhere it is not provided — a CMS-only install
// (`auth,audit,cms`), a test that mounts the form bare, a portal that never wires it —
// the form renders NO Library-copy section at all. Rendering nothing is the honest
// behaviour for a deployment with no library, not a disabled control that promises a
// feature the install does not have.
//
// THE VALUE IS AN ACCESSOR, NOT A COMPONENT. Solid's Context.Provider reads `value`
// once, untracked, so a component passed directly could not follow the manifest's
// arrival (`caps.has("lib")` is false while GET /api/capabilities is in flight and true
// only once it has answered). The shell passes `() => caps.has("lib") ? Picker : null`
// and the form calls the accessor inside its own reactive scope, so the section
// appears the moment the manifest names the library and never before.
import { createContext, useContext, type JSX } from "solid-js";

/** What the form hands the picker: the stored id (or ""), and the change it wants back. */
export interface LibraryEbookPickerProps {
  /** The publication's `libraryEbookId` as the form holds it — "" when none is linked. */
  value: string;
  /** The new id ("" clears the link); `label` is the chosen ebook's title when the picker knows it. */
  onChange: (id: string, label?: string) => void;
  /** Automation id on the control — the form passes `input-cms-publication-library-ebook`. */
  id?: string;
  /** The Field's validation message, when the server refused the id. */
  error?: string;
}

/** A picker over the library's ebooks — by contract, not by import: the shell decides which module backs it. */
export type LibraryEbookPicker = (props: LibraryEbookPickerProps) => JSX.Element;

/**
 * The slot's reactive value: the picker component, or null when this install has no
 * library to pick from. Called inside the form's reactive scope, so it may read the
 * capabilities manifest.
 */
export type LibraryEbookPickerSource = () => LibraryEbookPicker | null;

/** Renders nothing: an install without a library shows no Library-copy section, not an error. */
const UNFILLED: LibraryEbookPickerSource = () => null;

const LibraryEbookPickerContext = createContext<LibraryEbookPickerSource>(UNFILLED);

/**
 * The shell wraps its routes in this, passing `() => caps.has("lib") ? <its picker> : null`.
 * The CMS never provides a value of its own — the default is the empty hole.
 */
export const LibraryEbookPickerProvider = LibraryEbookPickerContext.Provider;

/**
 * Call in a component body; the result is an accessor — `null` means "no library on this
 * install, render nothing", a component means "mount it as
 * `<Picker value={libraryEbookId} onChange={…} />`".
 */
export const useLibraryEbookPicker = (): LibraryEbookPickerSource => useContext(LibraryEbookPickerContext);
